import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';

const GAP_TYPES=[
  'MISSING_PRIMARY_SOURCE',
  'STALE_EVIDENCE',
  'UNVERIFIED_CLAIM',
  'CONFLICTING_EVIDENCE',
  'MISSING_FINANCIAL_DATA',
  'MISSING_VALUATION_DATA',
  'MISSING_TIMING_CONTEXT',
  'MISSING_THESIS_BREAKER',
  'MISSING_EXPOSURE_DATA',
  'UNKNOWN'
];

const PRIORITY={NONE:0,LOW:1,REVIEW:2,MEDIUM:3,HIGH:4,CRITICAL:5};

function rank(x){return PRIORITY[x]||0}
function finite(v){return typeof v==='number'&&Number.isFinite(v)}

function normalizeGap(x){
  x=x||{};
  return Object.freeze({
    symbol:x.symbol||null,
    scope:['OWNED','WATCHLIST','RESEARCH'].includes(x.scope)?x.scope:'RESEARCH',
    gapType:GAP_TYPES.includes(x.gapType)?x.gapType:'UNKNOWN',
    stage:['BUSINESS','STOCK','TIMING','WHY_WRONG'].includes(x.stage)?x.stage:'BUSINESS',
    priority:Object.prototype.hasOwnProperty.call(PRIORITY,x.priority)?x.priority:'REVIEW',
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    ageHours:finite(x.ageHours)?Math.max(0,x.ageHours):null,
    sourceCount:finite(x.sourceCount)?Math.max(0,Math.floor(x.sourceCount)):0,
    independentSourceCount:finite(x.independentSourceCount)?Math.max(0,Math.floor(x.independentSourceCount)):0,
    criticalToDecision:x.criticalToDecision===true,
    note:x.note||null
  });
}

function requiredAction(gap){
  const g=normalizeGap(gap);
  const map={
    MISSING_PRIMARY_SOURCE:'FIND_PRIMARY_SOURCE',
    STALE_EVIDENCE:'REFRESH_EVIDENCE',
    UNVERIFIED_CLAIM:'VERIFY_CLAIM',
    CONFLICTING_EVIDENCE:'RESOLVE_CONFLICT',
    MISSING_FINANCIAL_DATA:'FILL_FINANCIAL_DATA',
    MISSING_VALUATION_DATA:'FILL_VALUATION_DATA',
    MISSING_TIMING_CONTEXT:'FILL_TIMING_CONTEXT',
    MISSING_THESIS_BREAKER:'DEFINE_THESIS_BREAKER',
    MISSING_EXPOSURE_DATA:'FILL_EXPOSURE_DATA',
    UNKNOWN:'INVESTIGATE_GAP'
  };
  return map[g.gapType];
}

function closureState(gap){
  const g=normalizeGap(gap);

  if(g.gapType==='CONFLICTING_EVIDENCE'){
    if(g.independentSourceCount>=2 && g.verification==='VERIFIED') return 'REVIEW_CONFLICT';
    return 'OPEN';
  }

  if(g.gapType==='STALE_EVIDENCE'){
    if(g.ageHours!==null && g.ageHours<=72 && g.verification==='VERIFIED') return 'CLOSED';
    return 'OPEN';
  }

  if(g.gapType==='MISSING_THESIS_BREAKER'){
    return g.verification==='VERIFIED' ? 'CLOSED' : 'OPEN';
  }

  if(g.sourceCount>=1 && g.verification==='VERIFIED') return 'CLOSED';
  if(g.sourceCount>=1 && g.verification==='PARTIAL') return 'PARTIAL';
  return 'OPEN';
}

function urgency(gap){
  const g=normalizeGap(gap);
  const state=closureState(g);
  if(state==='CLOSED') return 'NONE';
  if(g.criticalToDecision && g.scope==='OWNED') return 'NOW';
  if(g.criticalToDecision) return 'SOON';
  if(rank(g.priority)>=rank('HIGH') && g.scope==='OWNED') return 'NOW';
  if(rank(g.priority)>=rank('MEDIUM')) return 'SOON';
  return 'WHEN_RELEVANT';
}

function buildGapTask(gap){
  const g=normalizeGap(gap);
  return Object.freeze({
    symbol:g.symbol,
    scope:g.scope,
    stage:g.stage,
    gapType:g.gapType,
    action:requiredAction(g),
    closureState:closureState(g),
    urgency:urgency(g),
    priority:g.priority,
    criticalToDecision:g.criticalToDecision,
    tradeSignal:false,
    capitalPermission:false,
    entryGateOverride:false,
    companyQualityOverride:false
  });
}

function dedupe(gaps){
  const rows=Array.isArray(gaps)?gaps:[];
  const best=new Map();

  for(const g0 of rows){
    const g=normalizeGap(g0);
    const key=`${g.symbol||'UNKNOWN'}|${g.stage}|${g.gapType}`;
    const existing=best.get(key);
    if(!existing){
      best.set(key,g);
      continue;
    }

    const existingScore =
      rank(existing.priority) +
      (existing.criticalToDecision?3:0) +
      (existing.scope==='OWNED'?2:0);

    const candidateScore =
      rank(g.priority) +
      (g.criticalToDecision?3:0) +
      (g.scope==='OWNED'?2:0);

    if(candidateScore>existingScore) best.set(key,g);
  }

  return Object.freeze([...best.values()]);
}

function buildClosureQueue(gaps){
  const tasks=dedupe(gaps).map(buildGapTask).filter(t=>t.closureState!=='CLOSED');

  return Object.freeze(tasks.sort((a,b)=>{
    const urgencyRank={NONE:0,WHEN_RELEVANT:1,SOON:2,NOW:3};
    const u=urgencyRank[b.urgency]-urgencyRank[a.urgency];
    if(u!==0) return u;

    const owned=(b.scope==='OWNED'?1:0)-(a.scope==='OWNED'?1:0);
    if(owned!==0) return owned;

    return rank(b.priority)-rank(a.priority);
  }));
}

function decisionReadiness(symbol,gaps){
  const rows=(Array.isArray(gaps)?gaps:[]).filter(g=>normalizeGap(g).symbol===symbol);
  const tasks=rows.map(buildGapTask);
  const openCritical=tasks.filter(t=>t.closureState!=='CLOSED'&&t.criticalToDecision);
  const openAny=tasks.filter(t=>t.closureState!=='CLOSED');

  let state='READY_FOR_REVIEW';
  if(openCritical.length) state='BLOCKED_BY_EVIDENCE_GAP';
  else if(openAny.length) state='REVIEW_WITH_GAPS';

  return Object.freeze({
    symbol,
    state,
    openCritical:Object.freeze(openCritical),
    openCount:openAny.length,
    capitalPermission:false,
    tradeSignal:false
  });
}

function summary(gaps,maxItems){
  const max=finite(maxItems)?Math.max(1,Math.floor(maxItems)):5;
  const q=buildClosureQueue(gaps);

  return Object.freeze({
    next:q[0]||null,
    top:Object.freeze(q.slice(0,max)),
    hiddenCount:Math.max(0,q.length-max),
    counts:Object.freeze({
      open:q.filter(t=>t.closureState==='OPEN').length,
      partial:q.filter(t=>t.closureState==='PARTIAL').length,
      conflict:q.filter(t=>t.closureState==='REVIEW_CONFLICT').length,
      now:q.filter(t=>t.urgency==='NOW').length
    }),
    tradeSignal:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const primaryGap={
    symbol:'AAA',scope:'OWNED',gapType:'MISSING_PRIMARY_SOURCE',
    stage:'BUSINESS',priority:'HIGH',verification:'UNVERIFIED',
    sourceCount:0,criticalToDecision:true
  };
  const staleGap={
    symbol:'BBB',scope:'WATCHLIST',gapType:'STALE_EVIDENCE',
    stage:'TIMING',priority:'MEDIUM',verification:'VERIFIED',
    ageHours:200,sourceCount:1
  };
  const freshGap={
    symbol:'CCC',scope:'RESEARCH',gapType:'STALE_EVIDENCE',
    stage:'TIMING',priority:'REVIEW',verification:'VERIFIED',
    ageHours:24,sourceCount:1
  };
  const conflictGap={
    symbol:'DDD',scope:'OWNED',gapType:'CONFLICTING_EVIDENCE',
    stage:'WHY_WRONG',priority:'HIGH',verification:'VERIFIED',
    sourceCount:3,independentSourceCount:2,criticalToDecision:true
  };

  ok('missing primary source maps to source action',requiredAction(primaryGap)==='FIND_PRIMARY_SOURCE');
  ok('critical owned evidence gap urgency is NOW',urgency(primaryGap)==='NOW');
  ok('missing primary source remains open',closureState(primaryGap)==='OPEN');
  ok('stale evidence remains open',closureState(staleGap)==='OPEN');
  ok('fresh verified evidence closes stale gap',closureState(freshGap)==='CLOSED');
  ok('verified conflict remains explicit conflict review',closureState(conflictGap)==='REVIEW_CONFLICT');

  let task=buildGapTask(primaryGap);
  ok('gap task remains non-trading',task.tradeSignal===false);
  ok('gap task grants no capital permission',task.capitalPermission===false);
  ok('gap task never bypasses entry gate',task.entryGateOverride===false);
  ok('gap task never overrides company quality',task.companyQualityOverride===false);

  const q=buildClosureQueue([staleGap,freshGap,primaryGap,conflictGap]);
  ok('closed gaps are removed from queue',!q.some(x=>x.symbol==='CCC'));
  ok('urgent critical owned gap ranks first',q[0].symbol==='AAA'||q[0].symbol==='DDD');

  const dupes=dedupe([
    {...primaryGap,priority:'MEDIUM',criticalToDecision:false},
    primaryGap
  ]);
  ok('duplicate evidence gaps are compressed',dupes.length===1);
  ok('dedupe keeps stronger evidence gap',dupes[0].criticalToDecision===true);

  let ready=decisionReadiness('AAA',[primaryGap]);
  ok('critical evidence gap blocks decision readiness',ready.state==='BLOCKED_BY_EVIDENCE_GAP');
  ok('readiness never grants capital permission',ready.capitalPermission===false);

  ready=decisionReadiness('BBB',[staleGap]);
  ok('noncritical open gap produces review-with-gaps',ready.state==='REVIEW_WITH_GAPS');

  ready=decisionReadiness('CCC',[freshGap]);
  ok('closed gaps allow ready-for-review',ready.state==='READY_FOR_REVIEW');

  const s=summary([primaryGap,staleGap,conflictGap],2);
  ok('summary caps visible queue',s.top.length===2);
  ok('summary reports hidden gap tasks',s.hiddenCount===1);
  ok('summary remains non-trading',s.tradeSignal===false);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={
  VERSION,GAP_TYPES,normalizeGap,requiredAction,closureState,urgency,
  buildGapTask,dedupe,buildClosureQueue,decisionReadiness,summary,selfTest
};

try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestEvidenceGapClosureEngine=Object.freeze(API);

})(engineContext);
